import { API_URL } from "./constants";

export interface ErrorLog {
  id: string;
  timestamp: string;
  component: string;
  error_type: string;
  error_message: string;
  apiUrl?: string;
  userId?: string;
  email?: string;
  additionalData?: Record<string, any>;
}

class ErrorTracker {
  private static instance: ErrorTracker;

  private constructor() {}

  public static getInstance(): ErrorTracker {
    if (!ErrorTracker.instance) {
      ErrorTracker.instance = new ErrorTracker();
    }
    return ErrorTracker.instance;
  }

  public async addError(
    errorDetails: Omit<ErrorLog, "id" | "timestamp">
  ): Promise<void> {
    try {
      const error: Omit<ErrorLog, "id"> = {
        ...errorDetails,
        timestamp: new Date().toISOString(),
      };

      const response = await fetch(`${API_URL}/api/save-error-log`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(error),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("📝 ErrorTracker: Error logged successfully:", result);
    } catch (error) {
      console.error("❌ ErrorTracker: Failed to log error:", error);
      // Fallback to console log if API fails
      console.error(
        "📝 ErrorTracker: Error details (API failed):",
        errorDetails
      );
    }
  }

  public async getErrors(): Promise<ErrorLog[]> {
    try {
      const response = await fetch(`${API_URL}/api/get-error-logs`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("result", result.body.data);
      return result.body.data || [];
    } catch (error) {
      console.error("❌ ErrorTracker: Failed to retrieve errors:", error);
      return [];
    }
  }

  public async clearErrors(): Promise<void> {
    try {
      const response = await fetch(`${API_URL}/api/clear-error-logs`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      console.log("📝 ErrorTracker: All errors cleared successfully");
    } catch (error) {
      console.error("❌ ErrorTracker: Failed to clear errors:", error);
    }
  }

  public async deleteError(errorId: string): Promise<void> {
    try {
      const response = await fetch(`${API_URL}/api/clear-error-logs`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ errorId }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      console.log("📝 ErrorTracker: Error deleted successfully:", errorId);
    } catch (error) {
      console.error("❌ ErrorTracker: Failed to delete error:", error);
    }
  }
}

// Export singleton instance
export const errorTracker = ErrorTracker.getInstance();

// Convenience function for easy usage
export const logError = async (
  component: string,
  error_type: string,
  error_message: string,
  additionalData?: {
    apiUrl?: string;
    userId?: string;
    email?: string;
    [key: string]: any;
  }
): Promise<void> => {
  await errorTracker.addError({
    component,
    error_type,
    error_message,
    ...additionalData,
  });
};
